// React
import { createContext, FC, ReactElement, useCallback, useContext, useEffect, useState } from 'react';
// Modules
import { useRouter } from 'next/router';
// Contexts
import { useUser } from '@utmb/contexts/AuthContext';
// Interface
import { DashboardContextProps } from '@utmb/types/dashboard-context';
import { UserAccountTypeEnum } from '@utmb/types/enum/UserAccountTypeEnum';
import {
    DashboardMemberView,
    DashboardMemberInformationView,
    DashboardMemberPreferenceCenterView,
    DashboardMemberPreferencesView,
    DashboardOrganizerView,
} from '@utmb/types/index';
// Utils
import UserMemberSocialMediaDto from '@utmb/types/dto/UserMemberSocialMediaDto';
import DashboardMemberSocialNetworkView from '@utmb/types/view/DashboardMemberSocialNetworkView';
import { fetchTheAPI, TENANCY_DEFAULT } from '@utmb/utils/utmb';

const DashboardContext = createContext({} as DashboardContextProps);

export const DashboardProvider: FC = ({ children }): ReactElement => {
    const [isLoginMemberInProgress, setIsLoginMemberInProgress] = useState<boolean>(false);
    const [dashboardMember, setDashboardMember] = useState<DashboardMemberView | undefined>();
    const [dashboardOrganizer, setDashboardOrganizer] = useState<DashboardOrganizerView | undefined>();
    const [isAttached, setIsAttached] = useState<boolean | undefined>();
    const { asPath, locale } = useRouter();
    const user = useUser();

    const getMember = async (): Promise<DashboardMemberView | DashboardOrganizerView> => {
        const token = await user.getToken();

        const apiUrl = user.me?.accountType === UserAccountTypeEnum.ORGANIZER ? 'organizer' : 'member';

        return (await fetchTheAPI({
            isClient: true,
            path: `/users/dashboard/${apiUrl}`,
            params: { lang: locale! },
            bearer: token,
            tenantId: TENANCY_DEFAULT,
        })) as DashboardMemberView | DashboardOrganizerView;
    };

    const refreshMember = async (): Promise<void> => {
        setIsLoginMemberInProgress(true);
        try {
            const member = await getMember();

            if (member) {
                if (user.me?.accountType === UserAccountTypeEnum.ORGANIZER) {
                    setDashboardOrganizer(member as DashboardOrganizerView);
                } else {
                    setDashboardMember(member as DashboardMemberView);
                }
            }
        } catch (error) {
            console.error('Error refreshing member:', error);
        } finally {
            setIsLoginMemberInProgress(false);
        }
    };

    const initializeDashboard = useCallback(async (): Promise<void> => {
        if (isLoginMemberInProgress) return;
        if (dashboardMember || dashboardOrganizer) return;

        const isMyUTMBPage = asPath.match(/^\/my-utmb\//);

        if (user.isLoginInProgress) return;

        if (user.isAuthenticated && user.me?.email) {
            // Seulement charger les données si l'utilisateur a un accountType
            // La redirection est maintenant gérée par AuthProvider
            if (user.me?.accountType !== null) {
                await refreshMember();
            }
        } else {
            if (isMyUTMBPage) {
                user.login();
            }
            setIsLoginMemberInProgress(false);
        }
    }, [
        user.isLoginInProgress,
        user.isAuthenticated,
        user.me?.email,
        user.me?.accountType,
        dashboardMember,
        dashboardOrganizer,
        isLoginMemberInProgress,
        asPath,
    ]);

    useEffect(() => {
        setDashboardMember(undefined);
        setDashboardOrganizer(undefined);
    }, [user.me?.id]);

    useEffect(() => {
        if (user?.me?.accountType) {
            const { accountType, profileUrl } = user.me;

            // Verify if
            // - it's a Member or Organizer
            // - is the logged Member user is attached to a runner page
            const isRunnerPageAttached = accountType === UserAccountTypeEnum.MEMBER && !!profileUrl;

            setIsAttached(isRunnerPageAttached);
        }
    }, [user]);

    /**
     * Set new information in member
     * @param {DashboardMemberInformationView} information
     */
    const updateDashboardMemberInformation = (information: DashboardMemberInformationView, socials: UserMemberSocialMediaDto[] | null): void => {
        if (!dashboardMember) return;

        const newMemberInformation = Object.assign({}, dashboardMember.information, information);
        if (socials) {
            const newSocials: DashboardMemberSocialNetworkView[] = socials.map((s) => ({ type: s.social, url: s.url }));

            setDashboardMember({
                ...dashboardMember,
                information: newMemberInformation,
                socials: newSocials,
            });
        } else {
            setDashboardMember({
                ...dashboardMember,
                information: newMemberInformation,
            });
        }
    };

    /**
     * Set new preferences in member
     * @param {DashboardMemberPreferencesView} preferences
     */
    const updateDashboardMemberPreferences = (preferences: DashboardMemberPreferencesView): void => {
        if (!dashboardMember) return;

        const newMemberPreferences = Object.assign({}, dashboardMember.preferences, preferences);

        setDashboardMember({
            ...dashboardMember,
            preferences: newMemberPreferences,
        });
    };

    /**
     * Set new preference center in member
     * @param {DashboardMemberPreferenceCenterView} preferenceCenter
     */
    const updateDashboardMemberPreferenceCenter = (preferenceCenter: DashboardMemberPreferenceCenterView): void => {
        if (!dashboardMember) return;

        const newMemberPreferenceCenter = Object.assign({}, dashboardMember.preferenceCenter, preferenceCenter);

        setDashboardMember({
            ...dashboardMember,
            preferenceCenter: newMemberPreferenceCenter,
        });
        refreshMember(); //As it can change loyalty status
        user?.refreshMe();
    };

    /**
     * Set new information in organizer
     * @param {DashboardMemberInformationView} information
     */
    const updateDashboardOrganizerInformation = (information: DashboardOrganizerView): void => {
        if (!dashboardOrganizer) return;

        const newOrganizerInformation = Object.assign({}, dashboardOrganizer, information);

        setDashboardOrganizer(newOrganizerInformation);
    };

    return (
        <DashboardContext.Provider
            value={{
                isLoginMemberInProgress,
                dashboardMember,
                dashboardOrganizer,
                isAttached,
                setIsLoginMemberInProgress,
                updateDashboardMemberInformation,
                updateDashboardMemberPreferences,
                updateDashboardMemberPreferenceCenter,
                updateDashboardOrganizerInformation,
                refreshMember,
                initializeDashboard,
            }}
        >
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    const user = useUser();

    if (context === undefined) {
        throw new Error(`useDashboard must be used within an DashboardProvider`);
    }

    useEffect(() => {
        if (user.isLoginInProgress) return;
        if (!context.dashboardMember && !context.dashboardOrganizer && !context.isLoginMemberInProgress) {
            context.initializeDashboard();
        }
    }, [user]);

    return context;
};
